export enum ContractTermName {
    BlendedPSMAnd90DayFTCPP = 'BlendedPSMAnd90DayFTCPP',
    Revenue = 'Revenue',
    BundleRevenueDiscount = 'BundleRevenueDiscount',
    VendorSharePlayCountCalcStrategy = 'VendorSharePlayCountCalcStrategy',
    UMGGrowthTarget = 'UMGGrowthTarget',
    UMGPerformanceDiscount = 'UMGPerformanceDiscount',
    RateMultipliers = 'RateMultipliers',
    ASDRatchets = 'ASDRatchets',
    ThirdPartyDeductionCaps = 'ThirdPartyDeductionCaps',
    CapPRSdeductions = 'CapPRSdeductions',
    SubscriberVolumeDiscount = 'SubscriberVolumeDiscount',
    StreamingTaxDeduction = 'StreamingTaxDeduction'
}

export enum HelpInfoContentType {
    Paragraph = 'paragraph',
    List = 'list',
    ListItemTitle = 'listItemTitle',
    ListItemBody = 'listItemBody'
}

export interface IParagraph {
    type: HelpInfoContentType.Paragraph;
    body: string;
}

export interface IList {
    type: HelpInfoContentType.List;
    body: IListItem[];
}

export interface IListItem {
    [HelpInfoContentType.ListItemTitle]?: string;
    [HelpInfoContentType.ListItemBody]: string;
    children?: IList;
}

export interface ISection {
    sectionTitle?: string;
    sectionContent: (IParagraph | IList)[];
}

export interface ContractTermInfo {
    termName: string;
    termDescription: IParagraph[];
    sections?: ISection[];
}

export const CONTRACT_TERM_HELP_INFO_MAP: Map<string, ContractTermInfo> = new Map([
    [
        ContractTermName.BlendedPSMAnd90DayFTCPP,
        {
            termName: 'Blended PSM and 90 Day FT CPP',
            termDescription: [
                {
                    type: HelpInfoContentType.Paragraph,
                    body: 'These fields indicate where or not the Cores rates, on the prior step, included value for 90 Day FT CPP and Blended PSM. These fields are not editable. '
                }
            ],
            sections: [
                {
                    sectionTitle: '90 Day Free Trial Note',
                    sectionContent: [
                        {
                            type: HelpInfoContentType.Paragraph,
                            body: 'If 90 day free trials is turned off, then all 90 day free trial rates for this rate card will be automatically calculated in the 1P discounted rates tab.'
                        }
                    ]
                }
            ]
        }
    ],
    [
        ContractTermName.Revenue,
        {
            termName: 'Revenue',
            termDescription: [
                {
                    type: HelpInfoContentType.Paragraph,
                    body: 'This sections determines how the revenue prong will be calculated as an input for the revenue share vs. PSM royalty model.'
                }
            ],
            sections: [
                {
                    sectionTitle: 'Revenue Share Percentage',
                    sectionContent: [
                        {
                            type: HelpInfoContentType.Paragraph,
                            body: 'This is a percentage of revenue that is included in the revenue royalty pool for a revenue share vs PSM royalty calculations. For example, assuming $100k in revenue and a 55% revenue share percent, the revenue royalty pool is $55K.'
                        },
                        {
                            type: HelpInfoContentType.Paragraph,
                            body: 'Revenue is the money that we recieve for subscriptions, advertising, or even 3rd party payments. Normally there are multiple deductions (bad debt, chargebacks, app store fees, etc...) that reduces the revenue we include in our royalty calculation.'
                        }
                    ]
                },
                {
                    sectionTitle: 'Revenue Deductions',
                    sectionContent: [
                        {
                            type: HelpInfoContentType.Paragraph,
                            body: 'Bad-debt and Charge backs are monetary amounts we may be allowed to deduct from our Gross Revenue number, resulting in a Net Revenue used for royalties calculations.'
                        },
                        {
                            type: HelpInfoContentType.List,
                            body: [
                                {
                                    [HelpInfoContentType.ListItemTitle]: 'Bad Debt',
                                    [HelpInfoContentType.ListItemBody]:
                                        'Uncollectible revenue from subscription payments that customers failed to pay or defaulted on, resulting in lost revenue that was expected but never received.'
                                },
                                {
                                    [HelpInfoContentType.ListItemTitle]: 'Chargebacks',
                                    [HelpInfoContentType.ListItemBody]:
                                        'Customer-initiated payment reversals through their bank or credit card company that require the subscription service to return previously collected revenue.'
                                }
                            ]
                        }
                    ]
                }
            ]
        }
    ],
    [
        ContractTermName.BundleRevenueDiscount,
        {
            termName: 'Bundle Revenue Discount',
            termDescription: [
                {
                    type: HelpInfoContentType.Paragraph,
                    body: "The bundle revenue discount is a mechanism used to adjust royalty calculations when a music streaming service is bundled with other products or services. When a bundle is sold for more than the standalone music service price, this creates 'incremental revenue'. The bundle revenue discount allows the streaming service to exclude some or all of this incremental revenue from royalty calculations. It's calculated by subtracting the amount of incremental revenue that licensors get to participate in from the total incremental revenue. This discount is then applied to reduce the gross revenue figure used in royalty calculations."
                },
                {
                    type: HelpInfoContentType.Paragraph,
                    body: "The main purpose is to ensure that music rights holders don't automatically benefit from the full price increase when music is bundled with other services, allowing the streaming platform to retain some of the additional revenue generated by the bundle."
                }
            ],
            sections: [
                {
                    sectionTitle: 'Differentials',
                    sectionContent: [
                        {
                            type: HelpInfoContentType.Paragraph,
                            body: 'Differentials are defined ranges of incremental revenue where licensors may participate at different percentage levels. Differentials are expressed as percentages of the standalone music retail price.'
                        },
                        {
                            type: HelpInfoContentType.Paragraph,
                            body: 'For example, if the standalone music price is $10.99, a 9.1% differential would equal $1.00, and a 27.3% differential would equal $3.00, creating specific thresholds for how much incremental revenue the rights holders can participate in at different levels.'
                        }
                    ]
                },
                {
                    sectionTitle: 'Participation Levels',
                    sectionContent: [
                        {
                            type: HelpInfoContentType.Paragraph,
                            body: 'Participation levels determine how much of the incremental revenue within each differential range gets included in royalty calculations.'
                        }
                    ]
                }
            ]
        }
    ],
    [
        ContractTermName.VendorSharePlayCountCalcStrategy,
        {
            termName: 'Vendor Share and Play Count Strategy',
            termDescription: [
                {
                    type: HelpInfoContentType.Paragraph,
                    body: 'The vendor share and play count calculation strategy will determine how vendor pro-rata will be calculated and which plays will be included in the calculation. For each Vendor Share Strategy selected, earnings will be calculated for this contract (multiple strategies can be applied).'
                }
            ],
            sections: [
                {
                    sectionTitle: 'Vendor Share Strategy Definitions',
                    sectionContent: [
                        {
                            type: HelpInfoContentType.List,
                            body: [
                                {
                                    [HelpInfoContentType.ListItemTitle]: 'Standard',
                                    [HelpInfoContentType.ListItemBody]:
                                        'The standard pro-rata calculation is: Total Vendor Plays / Total Plays.'
                                },
                                {
                                    [HelpInfoContentType.ListItemTitle]: 'Anti dilution',
                                    [HelpInfoContentType.ListItemBody]:
                                        'Some contracts exclude non-musical plays in the pro-rata calculation, resulting in: (Total Vendor Plays - Total Vendor Non-music Plays) / (Total Music Plays - Total Non-music Plays)'
                                },
                                {
                                    [HelpInfoContentType.ListItemTitle]: 'Artist Centric 2025',
                                    [HelpInfoContentType.ListItemBody]:
                                        'This creates an additional calculation based on the Artist Centric play count definition below.'
                                },
                                {
                                    [HelpInfoContentType.ListItemTitle]: 'Artist Centric 2025 & Anti dilution',
                                    [HelpInfoContentType.ListItemBody]:
                                        'This creates an additional calculation based on the Artist Centric play count definition below.'
                                }
                            ]
                        }
                    ]
                },
                {
                    sectionTitle: 'Play Count Strategy Definitions',
                    sectionContent: [
                        {
                            type: HelpInfoContentType.List,
                            body: [
                                {
                                    [HelpInfoContentType.ListItemTitle]: 'Standard',
                                    [HelpInfoContentType.ListItemBody]:
                                        "Musical Plays greater than 30 seconds are included as 'plays'."
                                },
                                {
                                    [HelpInfoContentType.ListItemTitle]: 'Artist Centric 2025',
                                    [HelpInfoContentType.ListItemBody]:
                                        "Only plays that meet the below criteria are included as 'plays' in the Artist Centric calculation.",
                                    children: {
                                        type: HelpInfoContentType.List,
                                        body: [
                                            {
                                                [HelpInfoContentType.ListItemBody]: 'Plays greater than 30 seconds'
                                            },
                                            {
                                                [HelpInfoContentType.ListItemBody]:
                                                    'Minimum of 50 unique listeners in lookback period.'
                                            },
                                            {
                                                [HelpInfoContentType.ListItemBody]:
                                                    'Minimum of 1,000 plays, as defined above, in the lookback period'
                                            },
                                            {
                                                [HelpInfoContentType.ListItemBody]: 'Lookback Period = 12 Months'
                                            }
                                        ]
                                    }
                                }
                            ]
                        }
                    ]
                }
            ]
        }
    ],
    [
        ContractTermName.UMGGrowthTarget,
        {
            termName: 'UMG Growth Target',
            termDescription: [
                {
                    type: HelpInfoContentType.Paragraph,
                    body: "The Growth Target is a two-year deal structure specific to UMG 2024 AMU renewals. UMG receives a simplified 5.45% discount on royalties, instead of complex performance discounts, targeting $1.25B in total earnings from Amazon Music. If Amazon doesn't reach the growth target, UMG gets a true-up payment based on the difference between calculations under the old and new contract terms."
                }
            ]
        }
    ],
    [
        ContractTermName.UMGPerformanceDiscount,
        {
            termName: 'UMG Performance Discount',
            termDescription: [
                {
                    type: HelpInfoContentType.Paragraph,
                    body: 'Contractual terms that establish mechanisms to reduce AMU revenue share for UMG from 55% to 52%. Those are known as UMG Performance discounts. Revenue share discounts were introduced in 2021 deal renewal cycle, and consist of four discounts:'
                }
            ],
            sections: [
                {
                    sectionContent: [
                        {
                            type: HelpInfoContentType.List,
                            body: [
                                {
                                    [HelpInfoContentType.ListItemTitle]: 'Discount 1',
                                    [HelpInfoContentType.ListItemBody]: 'Meet 28MM AMU Subscriptions'
                                },
                                {
                                    [HelpInfoContentType.ListItemTitle]: 'Discount 2',
                                    [HelpInfoContentType.ListItemBody]:
                                        'Paid Ratio of AMU Subs vs. All Music subscriptions '
                                },
                                {
                                    [HelpInfoContentType.ListItemTitle]: 'Discount 3',
                                    [HelpInfoContentType.ListItemBody]: 'Churn'
                                },
                                {
                                    [HelpInfoContentType.ListItemTitle]: 'Discount 4',
                                    [HelpInfoContentType.ListItemBody]: 'Free Trial Conversion'
                                }
                            ]
                        }
                    ]
                }
            ]
        }
    ],
    [
        ContractTermName.RateMultipliers,
        {
            termName: 'Rate Multipliers',
            termDescription: [
                {
                    type: HelpInfoContentType.Paragraph,
                    body: 'Rate multipliers allow Origami to automatically calculate 1P discounted rates and 3P intro plans.'
                }
            ],
            sections: [
                {
                    sectionTitle: 'Promotional Trials',
                    sectionContent: [
                        {
                            type: HelpInfoContentType.Paragraph,
                            body: 'When running promotional (or free) trials, usually longer than 30 days, Amazon may recieve a discounted rate. This typically works by discounting the core plan rate value by a percentage to arrive at the final discounted rate.'
                        },
                        {
                            type: HelpInfoContentType.Paragraph,
                            body: 'For exmaple, if the core rate (PSM) for a music plan is $5.00, and our agreement states we must pay 25% the applicable PSM, the final rate paid would be $1.25.'
                        }
                    ]
                }
            ]
        }
    ],
    [
        ContractTermName.ThirdPartyDeductionCaps,
        {
            termName: 'Third Party Deduction Caps',
            termDescription: [
                {
                    type: HelpInfoContentType.Paragraph,
                    body: 'Some agreements may allow Amazon Music to discount the PSM rate for plans where another entity executes the subscription payment transactions for their customers. Typically this is a part of a carrier or reseller deal, also known as FUSE deals. These inputs will impact the rates and service revenue calculations on the 3P discount rates tab.'
                }
            ],
            sections: [
                {
                    sectionTitle: 'PSM Deduction Caps & Territory Overrides',
                    sectionContent: [
                        {
                            type: HelpInfoContentType.Paragraph,
                            body: 'The PSM discount is capped so that regardless of the reseller commision fee, we can only reduce the PSM rate down to that cap. Some contracts define territory specific PSM deduction caps which are captured in the territory orverride table.'
                        }
                    ]
                },
                {
                    sectionTitle: 'Revenue Deduction Caps',
                    sectionContent: [
                        {
                            type: HelpInfoContentType.Paragraph,
                            body: "Similar to PSM deduction caps, our contracts may allow discounts to subscription revenue in cases where app store or carrier/reseller fee's are deducted from the end user price. This revenue deduction is subject to a defined cap."
                        }
                    ]
                }
            ]
        }
    ],
    [
        ContractTermName.SubscriberVolumeDiscount,
        {
            termName: 'Subscriber Volume Discount',
            termDescription: [
                {
                    type: HelpInfoContentType.Paragraph,
                    body: 'During the 2019 renewals, there was a series of terms with our licensors that when we met certain subscriber thresholds (as defined in their contract) their revenue share/PSM will be reduced. Some labels agreed to 20MM and some agreed to 40MM subscriber threshold. Once the Global AMU subscriber volume exceeds the 20MM or 40MM threshold all royalties are adjusted downward by a derived percentage. This term is set to false by default.'
                }
            ]
        }
    ],
    [
        ContractTermName.ASDRatchets,
        {
            termName: 'ASD Ratchets',
            termDescription: [
                {
                    type: HelpInfoContentType.Paragraph,
                    body: 'Alexa Single Device (ASD) ratchets are variable rate structures usually associated with Major Label agreements. Generally, these structures determine different PSM rates depending on the ratio of ASD plan subscriptions to all paid plan subscriptions.'
                }
            ]
        }
    ],
    [
        ContractTermName.CapPRSdeductions,
        {
            termName: 'Cap PRS Deductions',
            termDescription: [
                {
                    type: HelpInfoContentType.Paragraph,
                    body: 'In certain countries with Performance Rights Societies (PRS), Amazon is required to report and pay royalties for revenue generating digital streaming activity. This is usually a percentage of revenue or a percentage of total roytalies due. Label deals allow for Amazon to deduct a capped portion of these PRS royalties from Label content fee payments, making it possible for Amazon to offset some of the cost associated with the PRS.'
                }
            ]
        }
    ],
    [
        ContractTermName.StreamingTaxDeduction,
        {
            termName: 'Streaming Tax Deductions',
            termDescription: [
                {
                    type: HelpInfoContentType.Paragraph,
                    body: "Allows Amazon Music to offset territory-specific streaming taxes (like France's 1.2% tax) by reducing royalty payments to labels. This helps recoup costs from regulatory taxes imposed on music streaming services."
                }
            ],
            sections: [
                {
                    sectionTitle: 'Deduction Models',
                    sectionContent: [
                        {
                            type: HelpInfoContentType.List,
                            body: [
                                {
                                    [HelpInfoContentType.ListItemTitle]: 'Gross Revenue',
                                    [HelpInfoContentType.ListItemBody]:
                                        'Tax deducted from gross revenue before royalty calculations.'
                                },
                                {
                                    [HelpInfoContentType.ListItemTitle]: 'Pro-Rata Share',
                                    [HelpInfoContentType.ListItemBody]:
                                        'Tax deducted from final royalty amount after calculations.'
                                }
                            ]
                        }
                    ]
                },
                {
                    sectionTitle: 'Territory Configuration',
                    sectionContent: [
                        {
                            type: HelpInfoContentType.Paragraph,
                            body: 'Each territory has its own tax rate and deduction cap.'
                        },
                        {
                            type: HelpInfoContentType.List,
                            body: [
                                {
                                    [HelpInfoContentType.ListItemTitle]: 'Deduction Cap',
                                    [HelpInfoContentType.ListItemBody]: 'Maximum percentage that can be deducted.'
                                },
                                {
                                    [HelpInfoContentType.ListItemTitle]: 'Tax Rate',
                                    [HelpInfoContentType.ListItemBody]:
                                        'Actual percentage used for royalty calculations.'
                                }
                            ]
                        }
                    ]
                }
            ]
        }
    ]
]);
